import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const LEVELS = [
  { label: 'Valley', zone: { min: 0.35, max: 0.65 }, speed: 0.8, points: 30, color: '#4a7fa5' },
  { label: 'Plateau', zone: { min: 0.55, max: 0.75 }, speed: 1.1, points: 60, color: '#a8cfe0' },
  { label: 'Summit', zone: { min: 0.72, max: 0.84 }, speed: 1.5, points: 100, color: '#c9a84c' },
  { label: 'Peak', zone: { min: 0.81, max: 0.89 }, speed: 2.0, points: 150, color: '#e8c46a' },
];

function getLevelIndex(round) {
  return Math.min(Math.floor((round - 1) / 3), LEVELS.length - 1);
}

export default function AltitudeGauge() {
  const [markerPos, setMarkerPos] = useState(0.1); // 0 = bottom, 1 = top
  const [direction, setDirection] = useState(1);
  const [running, setRunning] = useState(true);
  const [round, setRound] = useState(1);
  const [sessionPoints, setSessionPoints] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const posRef = useRef(0.1);
  const dirRef = useRef(1);
  const animRef = useRef(null);

  const levelData = LEVELS[getLevelIndex(round)];

  useEffect(() => {
    if (!running) return;
    let last = null;
    const step = (ts) => {
      if (last !== null) {
        const delta = ts - last;
        posRef.current += dirRef.current * levelData.speed * delta * 0.0004;
        if (posRef.current >= 0.98) { dirRef.current = -1; posRef.current = 0.98; }
        if (posRef.current <= 0.02) { dirRef.current = 1; posRef.current = 0.02; }
        setMarkerPos(posRef.current);
        setDirection(dirRef.current);
      }
      last = ts;
      animRef.current = requestAnimationFrame(step);
    };
    animRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animRef.current);
  }, [running, levelData.speed]);

  const handleMark = useCallback(() => {
    if (!running || result) return;
    setRunning(false);
    cancelAnimationFrame(animRef.current);

    const pos = posRef.current;
    const { zone, points } = levelData;
    const hit = pos >= zone.min && pos <= zone.max;

    // Bonus for centre
    const centre = (zone.min + zone.max) / 2;
    const proximity = 1 - Math.abs(pos - centre) / ((zone.max - zone.min) / 2);
    const earned = hit ? Math.round(points * (0.6 + proximity * 0.4)) : 0;

    setResult({ hit, earned, pos, zone });
    setHistory(h => [...h, { round, label: levelData.label, hit, earned }].slice(-8));
    if (hit) setSessionPoints(p => p + earned);
  }, [running, result, levelData, round]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === 'Space') handleMark(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleMark]);

  const nextRound = () => {
    setResult(null);
    setRound(r => r + 1);
    posRef.current = 0.05 + Math.random() * 0.15;
    dirRef.current = 1;
    setRunning(true);
  };

  const reset = () => {
    setResult(null);
    setRound(1);
    setSessionPoints(0);
    setHistory([]);
    posRef.current = 0.1;
    dirRef.current = 1;
    setRunning(true);
  };

  const GAUGE_H = 320;
  const markerY = GAUGE_H * (1 - markerPos);
  const zoneTop = GAUGE_H * (1 - levelData.zone.max);
  const zoneBot = GAUGE_H * (1 - levelData.zone.min);
  const zoneH = zoneBot - zoneTop;

  // Altitude labels
  const altLabels = [
    { pct: 0.9, label: '4500m', color: '#c9a84c' },
    { pct: 0.7, label: '3200m', color: '#a8cfe0' },
    { pct: 0.5, label: '2100m', color: '#7fa8c0' },
    { pct: 0.3, label: '1200m', color: '#4a7fa5' },
    { pct: 0.1, label: '400m', color: '#2c4a6a' },
  ];

  return (
    <div style={{ minHeight: '100vh', padding: '40px 0 80px' }}>
      <div className="container" style={{ maxWidth: '860px' }}>

        <div style={{ marginBottom: '32px', fontSize: '0.82rem', color: 'rgba(168,207,224,0.4)' }}>
          <Link to="/" style={{ color: 'rgba(168,207,224,0.4)' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <Link to="/activities" style={{ color: 'rgba(168,207,224,0.4)' }}>Activities</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: 'rgba(168,207,224,0.7)' }}>Altitude Gauge</span>
        </div>

        <div style={{ marginBottom: '36px' }}>
          <div className="label label-amber" style={{ marginBottom: '12px' }}>Timing · Observation</div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginBottom: '12px' }}>Altitude Gauge</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', fontSize: '1.2rem', marginBottom: '12px' }}>Mark the indicator inside the target zone</h2>
          <p style={{ maxWidth: '500px' }}>
            Mark the altitude when the indicator enters the target elevation zone. The zone narrows as you progress. Session points only.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="ag-layout">

          {/* Gauge display */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>

            {/* Level indicator */}
            <div style={{
              display: 'flex', gap: '8px', padding: '10px 20px',
              background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '999px',
            }}>
              {LEVELS.map((l, i) => (
                <div key={l.label} style={{
                  fontSize: '0.72rem', fontFamily: 'Syne, sans-serif', fontWeight: 700,
                  padding: '3px 10px', borderRadius: '999px',
                  background: i === getLevelIndex(round) ? 'rgba(201,168,76,0.2)' : 'transparent',
                  color: i === getLevelIndex(round) ? '#c9a84c' : 'rgba(168,207,224,0.3)',
                  letterSpacing: '0.05em',
                }}>
                  {l.label}
                </div>
              ))}
            </div>

            {/* The Gauge */}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>

              {/* Left: alt labels */}
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: `${GAUGE_H}px` }}>
                {altLabels.map(({ label, color }) => (
                  <div key={label} style={{ fontSize: '0.68rem', color, fontFamily: 'Syne, sans-serif', fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</div>
                ))}
              </div>

              {/* Gauge body */}
              <svg width="80" height={GAUGE_H} style={{ overflow: 'visible', flexShrink: 0 }}>
                {/* Track */}
                <rect x="25" y="0" width="30" height={GAUGE_H} rx="8" fill="rgba(26,53,80,0.8)" stroke="rgba(168,207,224,0.12)" strokeWidth="1" />

                {/* Altitude gradient fill */}
                <defs>
                  <linearGradient id="altGrad" x1="0" y1="1" x2="0" y2="0" gradientUnits="objectBoundingBox">
                    <stop offset="0%" stopColor="#2c4a6a" />
                    <stop offset="60%" stopColor="#4a7fa5" />
                    <stop offset="100%" stopColor="#c9a84c" />
                  </linearGradient>
                </defs>
                <rect x="26" y={markerY} width="28" height={GAUGE_H - markerY} rx="7" fill="url(#altGrad)" opacity="0.3" />

                {/* Target zone */}
                <rect x="24" y={zoneTop} width="32" height={zoneH} rx="4"
                  fill={`${levelData.color}22`}
                  stroke={levelData.color} strokeWidth="1.5" strokeDasharray="4 3" />

                {/* Zone label */}
                <text x="40" y={zoneTop - 6} textAnchor="middle" fontSize="9" fontFamily="Syne" fontWeight="700" fill={levelData.color}>{levelData.label}</text>

                {/* Tick marks */}
                {Array.from({ length: 10 }, (_, i) => (
                  <line key={i}
                    x1="20" y1={GAUGE_H * i / 10}
                    x2="24" y2={GAUGE_H * i / 10}
                    stroke="rgba(168,207,224,0.2)" strokeWidth="1"
                  />
                ))}

                {/* Marker */}
                <motion.g
                  style={{ y: markerY - 8 }}
                  animate={{ y: markerY - 8 }}
                  transition={{ type: 'tween', duration: 0 }}
                >
                  <polygon points="14,8 24,2 24,14" fill={levelData.color} />
                  <polygon points="56,8 46,2 46,14" fill={levelData.color} />
                  <rect x="24" y="3" width="22" height="10" rx="3" fill={levelData.color} opacity="0.8" />
                </motion.g>

                {/* Hit line */}
                {result && (
                  <line
                    x1="20" y1={GAUGE_H * (1 - result.pos)}
                    x2="60" y2={GAUGE_H * (1 - result.pos)}
                    stroke={result.hit ? '#80c880' : '#e07070'} strokeWidth="2" strokeDasharray="3 2"
                  />
                )}
              </svg>

              {/* Right: mark button */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                {running && !result && (
                  <motion.button
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.94 }}
                    onClick={handleMark}
                    style={{
                      writingMode: 'vertical-rl',
                      padding: '24px 14px',
                      background: '#c9a84c', color: '#0d1f35',
                      border: 'none', borderRadius: '12px',
                      fontFamily: 'Syne, sans-serif', fontWeight: 800,
                      fontSize: '0.85rem', cursor: 'pointer', letterSpacing: '0.1em',
                    }}
                  >
                    MARK
                  </motion.button>
                )}
                <div style={{ fontSize: '0.65rem', color: 'rgba(168,207,224,0.35)', textAlign: 'center', writingMode: 'vertical-rl', letterSpacing: '0.05em' }}>
                  or press Space
                </div>
              </div>
            </div>

            {/* Result */}
            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    textAlign: 'center',
                    background: result.hit ? 'rgba(100,200,100,0.08)' : 'rgba(200,80,80,0.08)',
                    border: `1px solid ${result.hit ? 'rgba(100,200,100,0.25)' : 'rgba(200,80,80,0.2)'}`,
                    borderRadius: '14px', padding: '18px 28px', minWidth: '220px',
                  }}
                >
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.3rem', fontWeight: 800, color: result.hit ? '#80c880' : '#e07070', marginBottom: '6px' }}>
                    {result.hit ? `+${result.earned} pts` : 'Missed zone'}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.5)', marginBottom: '14px' }}>
                    {result.hit ? `${levelData.label} zone reached` : `Try to hit the ${levelData.label} zone`}
                  </div>
                  <button onClick={nextRound} className="btn btn-primary" style={{ fontSize: '0.85rem', padding: '10px 24px' }}>
                    Next reading →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="card">
              <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Session Points</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '2.8rem', fontWeight: 800, color: '#c9a84c', lineHeight: 1 }}>{sessionPoints}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(168,207,224,0.35)', marginTop: '6px' }}>No monetary value</div>
            </div>

            <div className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Round</div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '1.4rem', fontWeight: 700, color: '#fff' }}>{round}</div>
                </div>
                <button onClick={reset} style={{ fontSize: '0.75rem', color: 'rgba(168,207,224,0.4)', background: 'none', border: '1px solid rgba(168,207,224,0.15)', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer' }}>
                  Reset
                </button>
              </div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.5)' }}>
                Target: <strong style={{ color: levelData.color }}>{levelData.label}</strong>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'rgba(168,207,224,0.4)' }}>
                Zone width: {Math.round((levelData.zone.max - levelData.zone.min) * 100)}% · {levelData.points} pts max
              </div>
            </div>

            {history.length > 0 && (
              <div className="card">
                <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Readings</div>
                {history.slice().reverse().map((h, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < history.length - 1 ? '1px solid rgba(168,207,224,0.06)' : 'none', fontSize: '0.8rem' }}>
                    <span style={{ color: 'rgba(168,207,224,0.5)' }}>#{h.round} {h.label}</span>
                    <span style={{ color: h.hit ? '#80c880' : '#e07070', fontWeight: 600 }}>{h.hit ? `+${h.earned}` : '—'}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ padding: '12px', background: 'rgba(168,207,224,0.03)', border: '1px solid rgba(168,207,224,0.07)', borderRadius: '10px', fontSize: '0.73rem', color: 'rgba(168,207,224,0.33)', lineHeight: 1.65 }}>
              Session points have no monetary value. Free entertainment only. Adults 18+ only.
            </div>
          </div>
        </div>
      </div>
      <style>{`@media(min-width:768px){.ag-layout{grid-template-columns:1fr 260px !important;}}`}</style>
    </div>
  );
}
